import { useTranslation } from "react-i18next";
import {
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Row,
  Space,
  Tag,
  Typography
} from "antd";
import { message } from "antd/lib";
import { isEmpty } from "lodash";
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState
} from "react";
import { v4 as uuidv4 } from 'uuid';
import {
  getAllMoocUniversity,
  getLecturersFromEnterprises,
} from "../../../../service/assign-course-construction";
import { getAllIndustry } from "../../../../service/course-construct";
import { globalStore, useAuthStore } from "../../../../stores/stores";
import { Enterprises } from "../../../../types/assign-course-construction";
import { ConstructCourseDetailType } from "../../../../types/course-construction";
import { UrlPattern } from "../../../../utils/regex";
import DrawerCustom from "../../../drawer/Drawer";
import FormItemDropdown from "../../../form-dropdown/FormItemDropdown";
import FormInput from "../../../form-input/FormInput";
import "./formCreateCourse.scss";

type FormCreateCourseProps = {
  enterprises: Enterprises[];
  industryGroup: Enterprises[];
  courseLevel: Enterprises[];
  coursePlan: Enterprises[];
  isOpenedEdit?: boolean;
  id: number;
  detailData: ConstructCourseDetailType;
  isOpened: boolean;
  setIsOpenedEdit: (data: boolean) => void;
  setIsOpened: React.Dispatch<React.SetStateAction<boolean>>;
  setDetailData: React.Dispatch<React.SetStateAction<ConstructCourseDetailType>>;
  setDataUpdate: (data: any) => void;
  setDataSponsorUpdate: (data: any) => void;
};

type StartSchool = {
  id: string;
  name: string;
};

const validations = {
  name: {
    required: true,
    validator: (_: unknown, value: string) => {
      if (value && value.length < 6)
        return Promise.reject(new Error("Tên khóa học phải từ 6 ký tự trở lên"));
      if (value && value.length > 255)
        return Promise.reject(new Error("Tên khóa học không vượt quá 255 ký tự"));
      if (!value) return Promise.reject(new Error("Vui lòng nhập tên khóa học"));
      return Promise.resolve();
    },
  },
  code: {
    required: true,
    validator: (_: unknown, value: string) => {
      if (!isEmpty(value) && !new RegExp(UrlPattern).test(value)) {
        return Promise.reject(new Error("Định dạng Mã khóa học không đúng, vui lòng nhập lại"));
      }
      if (isEmpty(value)) return Promise.reject(new Error("Vui lòng nhập mã khóa học"));
      return Promise.resolve();
    },
  },
  department: { required: true, message: "Vui lòng chọn khoa khóa học áp dụng" },
  field: { required: true, message: "Vui lòng chọn chuyên ngành áp dụng" },
  courseLevel: { required: true, message: "Vui lòng chọn cấp độ khóa học" },
  units: { required: true, message: "Vui lòng chọn đơn vị công tác" },
  hostSchool: { required: true, message: "Vui lòng chọn trường chủ trì" },
  lecturerName: { required: true, message: "Vui lòng chọn giảng viên" },
  coopSchool: { required: true, message: "Vui lòng chọn trường hợp tác" },
};

const getDisabledOptions = (selected: (string | number)[], options: any[]) =>
  options?.map((opt) => ({
    ...opt,
    disabled: selected.includes(opt.assignUserId),
  })) ?? [];

export const FormCreateCourse: FunctionComponent<FormCreateCourseProps> = ({
  enterprises,
  industryGroup = [],
  courseLevel = [],
  coursePlan = [],
  id,
  detailData,
  isOpened,
  isOpenedEdit = false,
  setIsOpenedEdit,
  setIsOpened,
  setDetailData,
  setDataUpdate,
  setDataSponsorUpdate,
}) => {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const { personalInfo } = globalStore();
  const permissionsRole = personalInfo?.role ?? [];
  const [form] = Form.useForm();
  const [hadPlan, setHadPlan] = useState(false);
  const [isSync, setIsSync] = useState(false);

  const [countSchoolSponsor, setCountSchoolSponsor] = useState([
    {
      assigned: [
        {
          id: uuidv4(),
          assignUserId: null,
          assignUserName: "",
          construct: true,
          instruct: false,
        },
      ],
      enterpriseId: user?.enterpriseUuid ?? "",
      enterpriseName: "",
      isSponsor: true,
    },
  ]);
  const [cooperativeSchools, setCooperativeSchools] = useState<any[]>([]);
  const [lecturerOptions, setLecturerOptions] = useState<Record<string, any[]>>({});
  const [enterpriseUniversity, setEnterpriseUniversity] = useState<StartSchool[]>([]);
  const [industry, setIndustry] = useState<Enterprises[]>([]);
  const [missingLecturerError, setMissingLecturerError] = useState(false);

  useEffect(() => {
    const fetchUniversities = async () => {
      const res = await getAllMoocUniversity();
      if (res.status === 200) {
        const list = res.data?.data
          ?.map((item: any) => ({ id: item.uuid, name: item.name }))
          ?.filter((u: any) => u.id !== enterprises?.[0]?.id);
        setEnterpriseUniversity(list);
      }
    };
    if (enterprises?.length) fetchUniversities();
  }, [enterprises]);

  const fetchLecturers = useCallback(async (enterpriseUUID: string) => {
    if (!enterpriseUUID) return;
    const data = {
      keyword: "",
      page: 1,
      size: 10000000,
      enterpriseUUID,
    };
    const res = await getLecturersFromEnterprises(data);
    setLecturerOptions((prev) => ({ ...prev, [enterpriseUUID]: res.data?.content ?? [] }));
  }, []);

  useEffect(() => {
    if (!isOpened) return;

    form.resetFields();
    setMissingLecturerError(false);

    const sponsorUUID = user?.enterpriseUuid ?? enterprises?.[0]?.id;
    fetchLecturers(sponsorUUID);

    if (isOpenedEdit && detailData) {
      hydrateFormWithDetail(detailData);

    } else {
      setCooperativeSchools([]);
      setCountSchoolSponsor([
        {
          assigned: [
            {
              id: uuidv4(),
              assignUserId: null,
              assignUserName: "",
              construct: true,
              instruct: false,
            },
          ],
          enterpriseId: "",
          enterpriseName: "",
          isSponsor: true,
        },
      ]);
      if (permissionsRole === "is_giang_vien" && user?.id) {
        form.setFieldValue("lecturerName-0", user.id);
        form.setFieldValue("hostSchool", sponsorUUID);

        fetchLecturers(sponsorUUID);
        setCountSchoolSponsor([
          {
            assigned: [
              {
                id: uuidv4(),
                assignUserId: user?.id as any,
                assignUserName: String(user.hoVaTen),
                construct: true,
                instruct: false,
              },
            ],
            enterpriseId: sponsorUUID,
            enterpriseName: "",
            isSponsor: true,
          },
        ]);
      }

      else if (permissionsRole === "is_qtcs" && user?.id) {
        form.setFieldValue("hostSchool", sponsorUUID);
        fetchLecturers(sponsorUUID);

        setCountSchoolSponsor([
          {
            assigned: [
              {
                id: uuidv4(),
                assignUserId: null,
                assignUserName: "",
                construct: true,
                instruct: false,
              },
            ],
            enterpriseId: sponsorUUID,
            enterpriseName: "",
            isSponsor: true,
          },
        ]);
      }

      else {
        form.setFieldValue("lecturerName-0", undefined);
      }
    }

  }, [isOpened]);

  const handleFacultyChange = async (facultyIds: string[]) => {
    if (!facultyIds?.length) return setIndustry([]);
    try {
      const res = await getAllIndustry(facultyIds);
      setIndustry(res.data?.data ?? []);
    } catch (err) {
      console.error("Error fetching industries", err);
      setIndustry([]);
    }
  };

  const addSchool = useCallback(() => {
    const visibleSchools = cooperativeSchools.filter((s) => s.display !== false);
    if (visibleSchools.length >= 5) return message.warning(t("Không thể tạo quá 5 trường hợp tác", "Không thể tạo quá 5 trường hợp tác"));
    setCooperativeSchools((prev) => [
      ...prev,
      {
        id: prev.length,
        display: true,
        enterpriseId: "",
        assigned: [
          {
            id: uuidv4(),
            assignUserId: null,
            assignUserName: "",
            construct: false,
            instruct: true,
          },
        ],
      },
    ]);
  }, [cooperativeSchools]);

  const removeSchool = useCallback((idx: number) => {
    setCooperativeSchools((prev) => prev.map((s, i) => (i === idx ? { ...s, display: false } : s)));
  }, []);

  const saveData = useCallback(() => {
    form
      .validateFields()
      .then(() => {
        if (isEmpty(countSchoolSponsor[0]?.assigned)) {
          setMissingLecturerError(true);
          return;
        }
        setMissingLecturerError(false);
        setDataUpdate(cooperativeSchools);
        setDataSponsorUpdate(countSchoolSponsor);
        form.submit();
      })
      .catch(() => void 0);
  }, [form, countSchoolSponsor, cooperativeSchools]);

  const closeForm = useCallback(() => {
    setDetailData({} as any);
    setIsOpened(false);
    setIsOpenedEdit(false);
    form.resetFields();
  }, []);

  const hydrateFormWithDetail = useCallback(
    (detail: ConstructCourseDetailType) => {
      const {
        name,
        code,
        faculties,
        industries,
        courseLevel: level,
        coursePlanId,
        assigners,
        isSynchronizeCourse,
      } = detail;

      form.setFieldsValue({
        name,
        code,
        industryGroup: faculties?.map((f: any) => f.id),
        industry: industries?.map((i: any) => i.id),
        courseLevel: level ? String(level) : null,
        coursePlanId,
      });

      if (coursePlanId) setHadPlan(true);
      setIsSync(!!isSynchronizeCourse);

      const sponsors = assigners?.filter((a: any) => a.isSponsor) ?? [];
      const cooperatives = assigners?.filter((a: any) => !a.isSponsor) ?? [];
      if (sponsors.length) {
        const sponsor = sponsors[0];
        const sponsorsWithId = sponsors.map((s: any) => ({
          ...s,
          assigned: s.assigned.map((t: any) => ({
            ...t,
            id: uuidv4(),
          })),
        }));

        setCountSchoolSponsor(sponsorsWithId);
        fetchLecturers(sponsor.enterpriseId);
        form.setFieldValue("hostSchool", sponsor.enterpriseId);

        sponsor.assigned?.forEach((teacher: any, idx: number) => {
          form.setFieldValue(`lecturerName-${idx}`, teacher.assignUserId ?? undefined);
        });
      }


      if (cooperatives.length) {
        const cooperativesWithId = cooperatives.map((school: any) => ({
          ...school,
          display: true,
          assigned: school.assigned.map((lecturer: any) => ({
            ...lecturer,
            id: uuidv4(),
          })),
        }));

        setCooperativeSchools(cooperativesWithId);

        cooperativesWithId.forEach((c: any, schoolIdx: number) => {
          form.setFieldValue(`cooperativeSchool${schoolIdx}`, c.enterpriseId);

          fetchLecturers(c.enterpriseId);
          c.assigned.forEach((t: any, lecIdx: number) => {
            form.setFieldValue(`lecturerCooperativeName${schoolIdx}-${t.id}`, t.assignUserId);
          });
        });
      }



      if (faculties?.length) {
        handleFacultyChange(faculties.map((f: any) => String(f.id)));
      }
    },
    [fetchLecturers]
  );

  const renderSponsorLecturers = () => {
    const sponsorLecturers = lecturerOptions[countSchoolSponsor[0].enterpriseId] ?? [];
    return countSchoolSponsor[0].assigned.map((item: any) => {
      const selectedIds = countSchoolSponsor[0].assigned.map((l: any) => l.assignUserId).filter(Boolean);
      const options = getDisabledOptions(selectedIds, sponsorLecturers);
      return (
        <Row gutter={16} key={item.id} align="middle" className="teacher-select-container mb-16"
          style={{ padding: "8px" }}
        >
          <div className="flex-1">
            <Form.Item
              name={`lecturerName-${item.id}`}
              label={t("Giảng viên", "Giảng viên")}
              rules={[validations.lecturerName]}
              initialValue={item.assignUserId}
            >
              <FormItemDropdown
                allowClear
                options={options}
                className="full-width"
                onChange={(val) => handleChangeTeacherSponsor(item.id, val)}
                placeholder={t("Chọn giảng viên", "Chọn giảng viên")}
                disabled={!countSchoolSponsor[0]?.enterpriseId}
              />
            </Form.Item>
          </div>
          <div className="mt-24">
            <div className="text-sync">
              <Checkbox
                checked={item.construct}
                onChange={(e) => handleCheckboxChangeSponsor(item.id, e, "construct")}
              />
              {t("Xây dựng", "Xây dựng")}
            </div>
          </div>
          <div className="mt-24">
            <div className="text-sync">
              <Checkbox
                checked={item.instruct}
                onChange={(e) => handleCheckboxChangeSponsor(item.id, e, "instruct")}
              />
              {t("Hướng dẫn", "Hướng dẫn")}
            </div>
          </div>
          {countSchoolSponsor[0].assigned.length > 1 && (
            <div className="mt-24">
              <Button
                icon={<DeleteOutlined style={{ color: "#ff4d4f" }} />}
                type="text"
                onClick={() => handleRemoveTeacherSponsor(item.id)}
              />
            </div>
          )}
        </Row>
      );
    });
  };

  const renderCooperativeSchools = () =>
    cooperativeSchools.map((school, schoolIdx) => {
      if (!school.display) return null;
      const coopLecturers = lecturerOptions[school.enterpriseId] ?? [];
      return (
        <div key={schoolIdx} className="mb-16">
          <Row gutter={16} className="mb-16"
            style={{ padding: "8px" }}
          >
            <div className="flex-1">
              <Form.Item
                name={`cooperativeSchool${schoolIdx}`}
                label={t("Trường hợp tác", "Trường hợp tác")}
                rules={[{ required: true, message: t("Vui lòng chọn trường hợp tác", "Vui lòng chọn trường hợp tác") }]}
              >
                <FormItemDropdown
                  allowClear
                  options={enterpriseUniversity}
                  placeholder={t("Chọn trường hợp tác", "Chọn trường hợp tác")}
                  onChange={(val) => {
                    setCooperativeSchools((prev) =>
                      prev.map((s, i) =>
                        i === schoolIdx
                          ? {
                            ...s,
                            enterpriseId: val,
                            assigned: [
                              {
                                id: uuidv4(),
                                assignUserId: null,
                                assignUserName: "",
                                construct: false,
                                instruct: false,
                              },
                            ],
                          }
                          : s
                      )
                    );
                    school.assigned.forEach((_: any, lecturerIdx: any) =>
                      form.setFieldValue(
                        `lecturerCooperativeName${schoolIdx}-${lecturerIdx}`,
                        null
                      )
                    );
                    fetchLecturers(val);
                  }}
                />
              </Form.Item>
            </div>
            <div className="ml-3 mt-24 flex align-center justify-center">
              <Button
                type="text"
                icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />}
                onClick={() => removeSchool(schoolIdx)}
              />
            </div>
          </Row>

          {
            school.assigned.map((lecturer: any, lecIdx: number) => {
              const selectedIds = school.assigned
                .map((l: any) => l.assignUserId)
                .filter(Boolean);
              const options = getDisabledOptions(selectedIds, coopLecturers);

              return (
                <Row key={lecturer.id} gutter={16} align="middle" className="teacher-select-container mb-16"
                  style={{ padding: "8px" }}

                >
                  <div className="flex-1">
                    <Form.Item
                      name={`lecturerCooperativeName${schoolIdx}-${lecturer.id}`}
                      label={t("Giảng viên", "Giảng viên")}
                      rules={[validations.lecturerName]}
                    >
                      <FormItemDropdown
                        allowClear
                        options={options}
                        placeholder={t("Chọn giảng viên", "Chọn giảng viên")}
                        className="full-width"
                        onChange={(val) => handleChangeTeacherCoop(schoolIdx, lecturer.id, val)}
                        disabled={!school.enterpriseId}
                      />
                    </Form.Item>
                  </div>
                  <div className="mt-24">
                    <Checkbox
                      checked={lecturer.construct}
                      onChange={(e) => handleCheckboxChangeCoop(schoolIdx, lecIdx, e, "construct")}
                    >{t("Xây dựng", "Xây dựng")}</Checkbox>
                  </div>
                  <div className="mt-24">
                    <Checkbox
                      checked={lecturer.instruct}
                      onChange={(e) => handleCheckboxChangeCoop(schoolIdx, lecIdx, e, "instruct")}
                    >{t("Hướng dẫn", "Hướng dẫn")}</Checkbox>
                  </div>
                  <div className="mt-24">
                    <Button
                      type="text"
                      icon={<DeleteOutlined style={{ color: "#ff4d4f" }} />}
                      onClick={() => handleRemoveTeacherCoop(schoolIdx, lecturer.id)}
                    />
                  </div>
                </Row>
              );

            })
          }
          <Tag
            className="mb-16 pointer"
            onClick={() => handleAddTeacherCoop(schoolIdx)}
          >
            <PlusOutlined /> {t("Thêm giảng viên", "Thêm giảng viên")}
          </Tag>
          {schoolIdx !== cooperativeSchools.length - 1 && <Divider />}
        </div >
      );
    });


  const handleAddTeacherSponsor = () => {
    setCountSchoolSponsor((prev) => {
      const copy = [...prev];
      copy[0].assigned.push({
        id: uuidv4(),
        assignUserId: null,
        assignUserName: "",
        construct: true,
        instruct: false,
      });
      return copy;
    });
  };


  const handleRemoveTeacherSponsor = (id: string) => {
    const nameToRemove = form.getFieldsValue();
    Object.keys(nameToRemove).forEach((key) => {
      if (key === `lecturerName-${id}`) {
        form.setFieldValue(key, undefined);
      }
    });

    setCountSchoolSponsor((prev) => {
      const copy = [...prev];
      copy[0].assigned = copy[0].assigned.filter((l) => l.id !== id);
      return copy;
    });
  };


  const handleChangeTeacherSponsor = (id: string, value: any) => {
    const sponsorUUID = countSchoolSponsor[0].enterpriseId;
    const match = lecturerOptions[sponsorUUID]?.find((l) => l.assignUserId === value);
    setCountSchoolSponsor((prev) => {
      const copy = [...prev];
      copy[0].assigned = copy[0].assigned.map((a) =>
        a.id === id ? { ...match, id, construct: a.construct ?? false, instruct: a.instruct ?? true } : a
      );
      return copy;
    });
  };


  const handleCheckboxChangeSponsor = (id: string, e: any, mode: "construct" | "instruct") => {
    setCountSchoolSponsor((prev) => {
      const copy = [...prev];
      const current = copy[0].assigned.find((l) => l.id === id);
      if (!current) return copy;
      const otherMode = mode === "construct" ? "instruct" : "construct";
      const newValue = e.target.checked;
      if (!newValue && !current[otherMode]) {
        message.warning(t("Phải chọn ít nhất 1 trong 2: Xây dựng hoặc Hướng dẫn", "Phải chọn ít nhất 1 trong 2: Xây dựng hoặc Hướng dẫn"));
        return copy;
      }
      current[mode] = newValue;
      return copy;
    });
  };


  const handleAddTeacherCoop = (schoolIdx: number) => {
    setCooperativeSchools((prev) =>
      prev.map((s, i) =>
        i === schoolIdx
          ? {
            ...s,
            assigned: [
              ...s.assigned,
              {
                id: uuidv4(),
                assignUserId: null,
                assignUserName: null,
                construct: false,
                instruct: true,
              },
            ],
          }
          : s
      )
    );
  };


  const handleRemoveTeacherCoop = (schoolIdx: number, lecturerId: string) => {
    const keys = Object.keys(form.getFieldsValue());
    keys.forEach((key) => {
      if (key.startsWith(`lecturerCooperativeName${schoolIdx}-lecturerId`)) {
        form.setFieldValue(key, undefined);
      }
    });

    setCooperativeSchools((prev) =>
      prev.map((s, i) =>
        i === schoolIdx
          ? {
            ...s,
            assigned: s.assigned.filter((lec: any) => lec.id !== lecturerId),
          }
          : s
      )
    );
  };


  const handleChangeTeacherCoop = (schoolIdx: number, lecturerId: string, value: any) => {
    const enterpriseId = cooperativeSchools[schoolIdx].enterpriseId;
    const match = lecturerOptions[enterpriseId]?.find((l) => l.assignUserId === value);

    setCooperativeSchools((prev) =>
      prev.map((s, i) =>
        i === schoolIdx
          ? {
            ...s,
            assigned: s.assigned.map((lec: any) =>
              lec.id === lecturerId
                ? {
                  ...match,
                  id: lecturerId,
                  construct: lec.construct,
                  instruct: true,
                }
                : lec
            ),
          }
          : s
      )
    );
  };

  const handleCheckboxChangeCoop = (
    schoolIdx: number,
    lecIdx: number,
    e: any,
    mode: "construct" | "instruct"
  ) => {
    setCooperativeSchools((prev) =>
      prev.map((s, i) =>
        i === schoolIdx
          ? {
            ...s,
            assigned: s.assigned.map((lec: any, j: any) => {
              if (j !== lecIdx) return lec;
              const otherMode = mode === "construct" ? "instruct" : "construct";
              const newValue = e.target.checked;
              if (!newValue && !lec[otherMode]) {
                message.warning(t("Phải chọn ít nhất 1 trong 2: Xây dựng hoặc Hướng dẫn", "Phải chọn ít nhất 1 trong 2: Xây dựng hoặc Hướng dẫn"));
                return lec;
              }
              return { ...lec, [mode]: newValue };
            }),
          }
          : s
      )
    );
  };

  return (
    <DrawerCustom
      title={id && id !== 0 ? t("Chỉnh sửa khóa học", "Chỉnh sửa khóa học") : t("Thêm mới khóa học", "Thêm mới khóa học")}
      open={isOpened}
      onClose={closeForm}
      widthDrawer={736}
      extraNode={
        <Space>
          <Button onClick={closeForm} className="btn btn-outlined">
            <CloseOutlined /> <Typography.Text>{t("Hủy", "Hủy")}</Typography.Text>
          </Button>
          <Button className="btn btn-primary" onClick={saveData} htmlType="submit">
            <SaveOutlined /> <Typography.Text>{t("Lưu", "Lưu")}</Typography.Text>
          </Button>
        </Space>
      }
      footerNode={
        <Button type="primary" onClick={addSchool} className="w-full">
          <PlusOutlined /> {t("Thêm Trường hợp tác", "Thêm Trường hợp tác")}
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        className="form-create-course"
        name="create-course-detail"
      >
        <Row gutter={16}>
          <Col span={12} className="mb-16">
            <Form.Item name="name" label={t("Tên khóa học", "Tên khóa học")} rules={[validations.name]}>
              <FormInput placeholder={t("Nhập tên khóa học", "Nhập tên khóa học")} />
            </Form.Item>
            <div className="label-down-text">{t("Tên hiển thị công khai cho khóa học của bạn", "Tên hiển thị công khai cho khóa học của bạn")}</div>
          </Col>
          <Col span={12} className="mb-16">
            <Form.Item name="code" label={t("Mã khóa học", "Mã khóa học")} rules={[validations.code]}>
              <FormInput placeholder={t("Nhập mã khóa học", "Nhập mã khóa học")} />
            </Form.Item>
            <div className="label-down-text">{t("Số định danh duy nhất xác định khoá học của bạn", "Số định danh duy nhất xác định khoá học của bạn")}</div>
          </Col>
          <Col span={24} className="mb-16">
            <Form.Item name="industryGroup" label={t("Khoa", "Khoa")} rules={[validations.department]}>
              <FormItemDropdown
                allowClear
                modeDropdown="multiple"
                options={industryGroup}
                placeholder={t("Chọn khoa khóa học áp dụng", "Chọn khoa khóa học áp dụng")}
                onChange={handleFacultyChange}
              />
            </Form.Item>
          </Col>
          <Col span={24} className="mb-16">
            <Form.Item name="industry" label={t("Chuyên ngành", "Chuyên ngành")} rules={[validations.field]}>
              <FormItemDropdown
                allowClear
                modeDropdown="multiple"
                options={industry}
                placeholder={t("Chọn chuyên ngành áp dụng", "Chọn chuyên ngành áp dụng")}
                disabled={!form.getFieldValue("industryGroup")?.length}
              />
            </Form.Item>
          </Col>
          <Col span={24} className="mb-16">
            <Form.Item name="courseLevel" label={t("Cấp độ", "Cấp độ")} rules={[validations.courseLevel]}>
              <FormItemDropdown
                allowClear options={courseLevel} placeholder={t("Chọn cấp độ khóa học", "Chọn cấp độ khóa học")} />
            </Form.Item>
          </Col>
          <Col span={12} className="mb-16">
            <Form.Item name="coursePlanId" label={t("Kế hoạch", "Kế hoạch")}>
              <FormItemDropdown
                allowClear
                options={coursePlan}

                placeholder={t("Chọn kế hoạch muốn đồng bộ", "Chọn kế hoạch muốn đồng bộ")}
                onChange={(val) => setHadPlan(!!val)}
              />
            </Form.Item>
            <div className="label-down-text">{t("Kế hoạch xây dựng đã được phê duyệt", "Kế hoạch xây dựng đã được phê duyệt")}</div>
          </Col>
          <Col span={12} className="check-box-sync mb-16">
            <Form.Item name="isSyncPlan">
              <div className={`text-sync ${hadPlan ? "" : "text-sync-grey"}`}>
                <Checkbox disabled={!hadPlan} checked={isSync} onChange={() => setIsSync((p) => !p)} /> {t("Đồng bộ cấu trúc khoá học", "Đồng bộ cấu trúc khoá học")}
              </div>
            </Form.Item>
          </Col>
        </Row>

        <Typography.Title level={5}>{t("Trường chủ trì khoá học", "Trường chủ trì khoá học")}</Typography.Title>
        <Row>
          <Col span={24} className="filter-school mb-16">
            <Form.Item name="hostSchool" label={t("Trường chủ trì", "Trường chủ trì")} rules={[validations.hostSchool]}>
              <FormItemDropdown
                allowClear
                options={enterprises}
                placeholder={t("Chọn trường chủ trì", "Chọn trường chủ trì")}
                onChange={(val) => {
                  setCountSchoolSponsor([
                    {
                      assigned: [
                        {
                          id: uuidv4(),
                          assignUserId: null,
                          assignUserName: "",
                          construct: true,
                          instruct: false,
                        },
                      ],
                      enterpriseId: val,
                      enterpriseName: "",
                      isSponsor: true,
                    },
                  ]);
                  form.setFieldValue("lecturerName-0", null);
                  fetchLecturers(val);
                }}

              />
            </Form.Item>
          </Col>
          <div className="w-full">{renderSponsorLecturers()}</div>
          <Tag className="mb-16 pointer" onClick={() => handleAddTeacherSponsor()}>
            <PlusOutlined /> {t("Thêm giảng viên", "Thêm giảng viên")}
          </Tag>

          {missingLecturerError && <p style={{ color: "red" }}>{t("Vui lòng chọn giảng viên", "Vui lòng chọn giảng viên")}</p>}
        </Row>

        {cooperativeSchools.some((s) => s.display) && (
          <>
            <Typography.Title level={5}>{t("Trường hợp tác xây dựng", "Trường hợp tác xây dựng")}</Typography.Title>
            {renderCooperativeSchools()}
          </>
        )}
      </Form>
    </DrawerCustom>
  );
};
